import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Container } from "react-bootstrap";
import { SearchAndFilterBar } from "../../components/search-and-filter-bar";
import { useTheme } from "../../contexts/ThemeContext";
import { useUser } from "../../contexts/UserContext";
import {
  getUserRole,
  getStatisticsTabIdsForRole,
  RESTRICTED_STATS_ROLES,
  ACCOUNTING_ROLES,
} from "../../utils/roleAccess";
import OverviewHeader from "./components/OverviewHeader";
import UsersDonutCard from "./components/UsersDonutCard";
import LoadsBarCard from "./components/LoadsBarCard";
import EarningsLineCard from "./components/EarningsLineCard";
import statsApi from "../../api/statsApi";
import statisticsPageService from "../../services/statisticsPageService";
import {
  mapSnapshotResponseToLoadsStats,
  mapSnapshotResponseToDashboard,
  mapSnapshotResponseToUsersLoadsStats,
} from "../../utils/statsSnapshotAdapter";
import {
  getDateRangeForPeriod,
  getLast5MonthsRangeUTC5,
  getPeriodPresetFromDateRange,
  getCurrentDateUTC5,
  getBarPeriod,
  getBarRequestRange,
  getBarDisplayPeriod,
  generateExpectedKeys,
  formatBarLabel,
  groupDaysIntoWeeks,
  buildApiParams,
  filtersSchemaByTab,
  getStatisticsResolvedDateRange,
} from "../../utils/statsFilterUtils";
import "./styles.scss";
import { StatCard } from "../../components/UI";
import Loading from "../../components/loading-animation";
import StatsFilters from "../../components/stats/StatsFilters";
import {
  buildStatisticsExportRows,
  downloadStatisticsXlsx,
} from "../../utils/statisticsExport";

function formatMonthLabel(monthKey) {
  if (!monthKey || monthKey.length < 7) return monthKey || "";
  const [y, m] = monthKey.split("-");
  const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function formatStatusLabel(s) {
  return String(s || "")
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function getLast5MonthsKeysUTC5() {
  const nowUTC5 = getCurrentDateUTC5();
  const keys = [];
  for (let i = 4; i >= 0; i--) {
    const monthDate = new Date(
      Date.UTC(nowUTC5.getUTCFullYear(), nowUTC5.getUTCMonth() - i, 1),
    );
    const year = monthDate.getUTCFullYear();
    const month = monthDate.getUTCMonth() + 1;
    keys.push(`${year}-${String(month).padStart(2, "0")}`);
  }
  return keys;
}

const LAST_VISUAL_MONTHS = 5;
const STATS_STORAGE_KEY = "stats-filters-v1";

const loadStatsFromStorage = () => {
  try {
    const raw = localStorage.getItem(STATS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
  } catch (_) {}
  return null;
};

const saveStatsToStorage = (data) => {
  try {
    localStorage.setItem(STATS_STORAGE_KEY, JSON.stringify(data));
  } catch (_) {}
};

const statisticsTabs = [
  { value: "users", label: "Users" },
  { value: "loads", label: "Load" },
  { value: "receivable", label: "Receivable" },
  { value: "payable", label: "Payable" },
  { value: "carriers", label: "Carriers" },
  { value: "customers", label: "Customers" },
];

const STATS_EXPORT_TAB_IDS = [
  "users",
  "loads",
  "receivable",
  "payable",
  "carriers",
  "customers",
];

// Helper function to format currency
const formatCurrency = (value) => {
  if (typeof value !== "number") return "$0.00";
  return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Helper function to format number
const formatNumber = (value) => {
  if (typeof value !== "number") return "0";
  return value.toLocaleString("en-US");
};

// Helper function to calculate percentage change
const calculateTrend = (current, previous) => {
  if (previous === undefined || previous === null || previous === 0) {
    if (current === 0) {
      return { type: "neutral", percentage: 0, text: "vs last period" };
    }
    return { type: "positive", percentage: 100, text: "vs last period" };
  }
  const change = ((current - previous) / previous) * 100;
  return {
    type: change > 0 ? "positive" : change < 0 ? "negative" : "neutral",
    percentage: Math.abs(change),
    text: "vs last period",
  };
};

const StatisticPage = () => {
  const { getThemeColors } = useTheme();
  const { user } = useUser();
  const colors = getThemeColors();
  const role = getUserRole(user);
  const allowedTabIds = useMemo(() => getStatisticsTabIdsForRole(role), [role]);
  const visibleTabs = useMemo(
    () => statisticsTabs.filter((tab) => allowedTabIds.includes(tab.value)),
    [allowedTabIds],
  );
  const defaultTab = visibleTabs[0]?.value ?? statisticsTabs[0].value;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTab, setSelectedTab] = useState(() => {
    const stored = loadStatsFromStorage();
    const tab = stored?.selectedTab;
    return tab && statisticsTabs.some((t) => t.value === tab) ? tab : defaultTab;
  });
  const [filtersByTab, setFiltersByTab] = useState(() => {
    const stored = loadStatsFromStorage();
    return stored?.filtersByTab ?? {};
  });
  const filterValues = filtersByTab[selectedTab] ?? {};
  const selectedDateRange = filterValues.dateRange ?? "today";

  const statisticsDateRangeDisplay = useMemo(
    () => getStatisticsResolvedDateRange(filterValues, selectedDateRange),
    [filterValues, selectedDateRange],
  );

  const updateFilterValues = useCallback(
    (updater) => {
      setFiltersByTab((prev) => {
        const current = prev[selectedTab] ?? {};
        const next = typeof updater === "function" ? updater(current) : updater;
        return { ...prev, [selectedTab]: next };
      });
    },
    [selectedTab]
  );
  const updateSelectedDateRange = useCallback(
    (value) => {
      setFiltersByTab((prev) => {
        const current = prev[selectedTab] ?? {};
        const next = { ...current, dateRange: value };
        if (value !== "custom") {
          next.period = getPeriodPresetFromDateRange(value);
          next.dateFrom = null;
          next.dateTo = null;
        }
        return { ...prev, [selectedTab]: next };
      });
    },
    [selectedTab]
  );

  const [topCardsStats, setTopCardsStats] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loadsStats, setLoadsStats] = useState(null);
  const [generalStats, setGeneralStats] = useState(null);
  const [carriersStats, setCarriersStats] = useState(null);
  const [customersStats, setCustomersStats] = useState(null);
  const [paymentsStats, setPaymentsStats] = useState(null);
  const [previousPeriodStats, setPreviousPeriodStats] = useState(null);
  const [usersCount, setUsersCount] = useState(null);
  const [usersByRole, setUsersByRole] = useState(null);
  const [top5Users, setTop5Users] = useState([]);
  const [barChartData, setBarChartData] = useState(null);
  const [barChartRawData, setBarChartRawData] = useState(null);
  const [barChartStatusData, setBarChartStatusData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isStatsFiltersOpen, setIsStatsFiltersOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState({});

  useEffect(() => {
    if (isStatsFiltersOpen && ["loads", "users", "receivable", "payable", "carriers", "customers"].includes(selectedTab)) {
      const period = getPeriodPresetFromDateRange(selectedDateRange);
      const base = {
        period: filterValues.period || period,
        startDate: filterValues.dateFrom
          ? `${filterValues.dateFrom}T00:00:00.000Z`
          : null,
        endDate: filterValues.dateTo
          ? `${filterValues.dateTo}T23:59:59.999Z`
          : null,
        scope:
          filterValues.scopeType === "global" || !filterValues.scopeType
            ? "global"
            : filterValues.scopeType,
        scopeId: filterValues.scopeId || null
      };
      const extended =
        selectedTab === "loads"
          ? { ...base, loadStatus: filterValues.loadStatus || [], shipmentType: filterValues.shipmentType || null }
          : selectedTab === "receivable" || selectedTab === "payable"
            ? { ...base, paymentStatus: filterValues.paymentStatus || [] }
            : selectedTab === "carriers"
              ? { ...base, carrierStatus: filterValues.carrierStatus || [] }
              : selectedTab === "customers"
                ? { ...base, customerStatus: filterValues.customerStatus || [] }
                : base;
      setDraftFilters(extended);
    }
  }, [isStatsFiltersOpen, selectedTab, selectedDateRange, filterValues]);

  const getDateRangeParams = () => {
    const now = new Date();
    let startDate, endDate;
    switch (selectedDateRange) {
      case "today":
        startDate = new Date(now);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        return {
          period: "day",
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        };
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        weekStart.setHours(0, 0, 0, 0);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        return {
          period: "day",
          startDate: weekStart.toISOString(),
          endDate: endDate.toISOString(),
        };
      case "this_month":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          0,
          23,
          59,
          59,
          999,
        );
        return {
          period: "month",
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        };
      case "this_quarter":
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        endDate = new Date(
          now.getFullYear(),
          (quarter + 1) * 3,
          0,
          23,
          59,
          59,
          999,
        );
        return {
          period: "month",
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        };
      case "this_year":
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
        return {
          period: "month",
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        };
      default:
        return { period: "month" };
    }
  };

  const getStatsSnapshotParams = (rangeParams) => {
    let from, to;
    if (selectedTab === "users") {
      const periodPreset = getPeriodPresetFromDateRange(selectedDateRange);
      const customStart = filterValues.dateFrom
        ? new Date(filterValues.dateFrom).toISOString()
        : null;
      const customEnd = filterValues.dateTo
        ? new Date(filterValues.dateTo).toISOString()
        : null;
      const usersRange = getDateRangeForPeriod(
        periodPreset,
        customStart,
        customEnd,
      );
      from = usersRange.startDate?.slice(0, 10);
      to = usersRange.endDate?.slice(0, 10);
    }
    if (!from || !to) {
      from = rangeParams?.startDate?.slice(0, 10);
      to = rangeParams?.endDate?.slice(0, 10);
    }
    if (!from || !to) {
      const now = new Date();
      from = new Date(now.getFullYear(), now.getMonth(), 1)
        .toISOString()
        .slice(0, 10);
      to = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        .toISOString()
        .slice(0, 10);
    }
    const base = { period: "range", from, to };
    if (
      selectedTab === "users" &&
      filterValues.scopeType === "user" &&
      filterValues.scopeId
    ) {
      const scopeId = String(filterValues.scopeId).trim();
      if (/^[a-fA-F0-9]{24}$/.test(scopeId)) {
        return { ...base, scopeType: "user", scopeId };
      }
    }
    return { ...base, scopeType: "global" };
  };

  // Get previous period params for comparison
  const getPreviousPeriodParams = () => {
    const now = new Date();
    let startDate, endDate;

    switch (selectedDateRange) {
      case "today":
        // Previous day
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = new Date(yesterday);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(yesterday);
        endDate.setHours(23, 59, 59, 999);
        return {
          period: "day",
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        };
      case "this_week":
        // Previous week
        const lastWeekStart = new Date(now);
        lastWeekStart.setDate(now.getDate() - now.getDay() - 7);
        lastWeekStart.setHours(0, 0, 0, 0);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        lastWeekEnd.setHours(23, 59, 59, 999);
        return {
          period: "day",
          startDate: lastWeekStart.toISOString(),
          endDate: lastWeekEnd.toISOString(),
        };
      case "this_month":
        // Previous month
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(
          now.getFullYear(),
          now.getMonth(),
          0,
          23,
          59,
          59,
          999,
        );
        return {
          period: "month",
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        };
      case "this_quarter":
        // Previous quarter
        const quarter = Math.floor(now.getMonth() / 3);
        const prevQuarter = quarter === 0 ? 3 : quarter - 1;
        const prevQuarterYear =
          quarter === 0 ? now.getFullYear() - 1 : now.getFullYear();
        startDate = new Date(prevQuarterYear, prevQuarter * 3, 1);
        endDate = new Date(
          prevQuarterYear,
          (prevQuarter + 1) * 3,
          0,
          23,
          59,
          59,
          999,
        );
        return {
          period: "month",
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        };
      case "this_year":
        // Previous year
        startDate = new Date(now.getFullYear() - 1, 0, 1);
        endDate = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
        return {
          period: "month",
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        };
      default:
        return null;
    }
  };

  const fetchStats = useCallback(async () => {
    const myId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    setBarChartData(null);
    setBarChartStatusData(null);
    const isAccountingOnly = ACCOUNTING_ROLES.includes(role);

    try {
      if (selectedTab === "loads" && allowedTabIds.includes("loads")) {
        const [result, topCardsResponse] = await Promise.all([
          statisticsPageService.fetchLoadsStats(
            filterValues,
            selectedDateRange,
          ),
          !isAccountingOnly
            ? statisticsPageService.fetchTopCardsStats()
            : Promise.resolve({ topCardsStats: null }),
        ]);
        if (fetchIdRef.current !== myId) return;
        if (result.loadsStats) setLoadsStats(result.loadsStats);
        if (result.top5Users) setTop5Users(result.top5Users);
        if (result.barChartData) setBarChartData(result.barChartData);
        if (result.barChartStatusData)
          setBarChartStatusData(result.barChartStatusData);
        if (result.dashboardStats) setDashboardStats(result.dashboardStats);
        if (topCardsResponse?.topCardsStats)
          setTopCardsStats(topCardsResponse.topCardsStats);
        setLoading(false);
        return;
      }

      if (selectedTab === "users" && allowedTabIds.includes("users")) {
        try {
          const result = await statisticsPageService.fetchUsersStats(
            filterValues,
            selectedDateRange,
          );
          if (fetchIdRef.current !== myId) return;
          if (result.loadsStats) setLoadsStats(result.loadsStats);
          if (result.top5Users) setTop5Users(result.top5Users);
          if (result.barChartData) setBarChartData(result.barChartData);
          if (result.barChartStatusData)
            setBarChartStatusData(result.barChartStatusData);
          if (result.dashboardStats) setDashboardStats(result.dashboardStats);
        } catch (err) {
          if (fetchIdRef.current !== myId) return;
          if (
            err?.response?.status === 403 ||
            err?.message === "Access denied"
          ) {
            setError("Access denied");
          } else {
            console.error("Error fetching users stats:", err);
            setError(err.message || "Failed to load statistics");
          }
        }
        setLoading(false);
        return;
      }

      if (selectedTab === "receivable" || selectedTab === "payable") {
        const result = await statisticsPageService.fetchPaymentsStats(
          filterValues,
          selectedDateRange,
          selectedTab,
        );
        if (fetchIdRef.current !== myId) return;
        if (result.paymentsStats) setPaymentsStats(result.paymentsStats);
        setLoading(false);
        return;
      }

      if (selectedTab === "carriers" && allowedTabIds.includes("carriers")) {
        const [result, topCardsResponse] = await Promise.all([
          statisticsPageService.fetchCarriersStats(
            filterValues,
            selectedDateRange,
          ),
          !isAccountingOnly
            ? statisticsPageService.fetchTopCardsStats()
            : Promise.resolve({ topCardsStats: null }),
        ]);
        if (fetchIdRef.current !== myId) return;
        if (result.carriersStats) setCarriersStats(result.carriersStats);
        if (result.loadsStats) setLoadsStats(result.loadsStats);
        if (topCardsResponse?.topCardsStats)
          setTopCardsStats(topCardsResponse.topCardsStats);
        setLoading(false);
        return;
      }

      if (selectedTab === "customers" && allowedTabIds.includes("customers")) {
        const [result, topCardsResponse] = await Promise.all([
          statisticsPageService.fetchCustomersStats(
            filterValues,
            selectedDateRange,
          ),
          !isAccountingOnly
            ? statisticsPageService.fetchTopCardsStats()
            : Promise.resolve({ topCardsStats: null }),
        ]);
        if (fetchIdRef.current !== myId) return;
        if (result.customersStats) setCustomersStats(result.customersStats);
        if (result.loadsStats) setLoadsStats(result.loadsStats);
        if (topCardsResponse?.topCardsStats)
          setTopCardsStats(topCardsResponse.topCardsStats);
        setLoading(false);
        return;
      }

      if (!isAccountingOnly) {
        const topCardsResponse =
          await statisticsPageService.fetchTopCardsStats();
        if (fetchIdRef.current !== myId) return;
        if (topCardsResponse.topCardsStats)
          setTopCardsStats(topCardsResponse.topCardsStats);
      }
    } catch (err) {
      if (fetchIdRef.current !== myId) return;
      console.error("Error fetching statistics:", err);
      setError(err.message || "Failed to load statistics");
    } finally {
      if (fetchIdRef.current === myId) setLoading(false);
    }
  }, [role, selectedDateRange, selectedTab, filterValues, allowedTabIds]);

  useEffect(() => {
    if (!allowedTabIds.includes(selectedTab)) {
      setSelectedTab(defaultTab);
    }
  }, [allowedTabIds, defaultTab, selectedTab]);

  useEffect(() => {
    setSearchTerm("");
  }, [selectedTab]);

  useEffect(() => {
    saveStatsToStorage({ filtersByTab, selectedTab });
  }, [filtersByTab, selectedTab]);

  useEffect(() => {
    if (allowedTabIds.includes("users")) {
      statsApi
        .getUsersByRole()
        .then((res) => {
          if (res?.data?.success && res?.data?.data) {
            setUsersByRole(res.data.data);
            if (res.data.data.totalUsers != null) {
              setUsersCount(res.data.data.totalUsers);
            }
          }
        })
        .catch(() => {});
    }
  }, [allowedTabIds]);

  const fetchStatsRef = useRef(fetchStats);
  const isFetchingRef = useRef(false);
  const lastFetchParamsRef = useRef(null);
  const hasInitialFetchedRef = useRef(false);
  const hasMountFallbackRequestRef = useRef(false);
  const fetchIdRef = useRef(0);

  useEffect(() => {
    fetchStatsRef.current = fetchStats;
  }, [fetchStats]);

  useEffect(() => {
    const fetchParams = JSON.stringify({
      selectedTab,
      selectedDateRange,
      filterValues: JSON.stringify(filterValues),
    });

    if (lastFetchParamsRef.current === fetchParams) {
      return;
    }

    lastFetchParamsRef.current = fetchParams;
    const isFirstFetch = !hasInitialFetchedRef.current;
    if (isFirstFetch) hasInitialFetchedRef.current = true;

    let cancelled = false;
    const runFetch = () => {
      if (cancelled || !fetchStatsRef.current) return;
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);
      Promise.resolve()
        .then(() => fetchStatsRef.current())
        .catch((err) => {
          console.error("Fetch stats error:", err);
          setError(err?.message || "Failed to load statistics");
        })
        .finally(() => {
          if (!cancelled) {
            isFetchingRef.current = false;
            setLoading(false);
          }
        });
    };

    const delay = isFirstFetch ? 0 : 150;
    const timeoutId = setTimeout(runFetch, delay);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [selectedTab, selectedDateRange, filterValues]);

  useEffect(() => {
    if (selectedTab !== "loads") return;
    if (loading) return;
    if (hasMountFallbackRequestRef.current) return;
    if (loadsStats && (loadsStats.total ?? 0) > 0) return;

    hasMountFallbackRequestRef.current = true;
    const fallbackFilters = {
      ...filterValues,
      period: "day",
      dateRange: "today",
      scopeType: "global",
      scopeId: null,
      dateFrom: null,
      dateTo: null,
    };

    statisticsPageService
      .fetchLoadsStats(fallbackFilters, "today")
      .then((result) => {
        if (result?.loadsStats) setLoadsStats(result.loadsStats);
        if (result?.top5Users) setTop5Users(result.top5Users);
        if (result?.barChartData) setBarChartData(result.barChartData);
        if (result?.barChartStatusData) setBarChartStatusData(result.barChartStatusData);
        if (result?.dashboardStats) setDashboardStats(result.dashboardStats);
      })
      .catch(() => {});
  }, [selectedTab, loading, loadsStats, selectedDateRange, filterValues]);

  useEffect(() => {
    if (!loading) return;
    const safetyTimeout = setTimeout(() => {
      setLoading(false);
      isFetchingRef.current = false;
    }, 25000);
    return () => clearTimeout(safetyTimeout);
  }, [loading]);

  useEffect(() => {
    const interval = setInterval(
      () => {
        if (selectedTab === "loads" && allowedTabIds.includes("loads")) {
          if (fetchStatsRef.current) {
            fetchStatsRef.current();
          }
        }
      },
      2 * 60 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [selectedTab, allowedTabIds]);

  const overviewFromDashboard = useMemo(() => {
    if (!dashboardStats) return {};
    if (dashboardStats.overview) return dashboardStats.overview;
    return {
      totalLoads: dashboardStats?.loads?.total ?? 0,
      totalRevenue: dashboardStats?.payments?.receivable?.totalAmount ?? 0,
      totalProfit: dashboardStats?.payments?.profit ?? 0,
    };
  }, [dashboardStats]);

  const previousOverviewFromDashboard = useMemo(() => {
    if (!previousPeriodStats) return {};
    if (previousPeriodStats.overview) return previousPeriodStats.overview;
    return {
      totalLoads: previousPeriodStats?.loads?.total ?? 0,
      totalRevenue: previousPeriodStats?.payments?.receivable?.totalAmount ?? 0,
      totalProfit: previousPeriodStats?.payments?.profit ?? 0,
    };
  }, [previousPeriodStats]);

  const usersDataResolved = useMemo(() => {
    const users = generalStats?.users ?? generalStats;
    return {
      usersData: users,
      totalUsers: generalStats?.totalUsers ?? users?.totalUsers ?? 0,
      topUsers: users?.topUsers ?? [],
    };
  }, [generalStats]);

  const getDefaultStatCards = useMemo(() => {
    const allTime = topCardsStats ?? {
      totalLoads: 0,
      grossRevenue: 0,
      totalProfit: 0,
    };
    const totalLoads = allTime.totalLoads ?? 0;
    const deliveredLoads =
      dashboardStats?.loads?.byStatus?.Delivered ??
      dashboardStats?.loads?.summary?.loadsByStatus?.delivered ??
      0;
    const deliveryRate =
      totalLoads > 0 ? Math.round((deliveredLoads / totalLoads) * 100) : 0;
    const allTimeTrend = { type: "neutral", percentage: 0, text: "All time" };
    return () => [
      {
        title: "Total Loads",
        value: formatNumber(totalLoads),
        trend: allTimeTrend,
        variant: "white",
        diagramType: "progress",
        diagramData: {
          value: deliveryRate,
          label: `${deliveryRate}%`,
          size: 60,
        },
      },
      {
        title: "Gross Revenue",
        value: formatCurrency(allTime.grossRevenue ?? 0),
        trend: allTimeTrend,
        variant: "blue",
        diagramType: "area",
        diagramData: {
          data: (
            dashboardStats?.revenueChart || [80, 50, 70, 60, 90, 75, 85]
          ).slice(-LAST_VISUAL_MONTHS),
          width: 125,
          height: 60,
        },
      },
      {
        title: "Total Profit",
        value: formatCurrency(allTime.totalProfit ?? 0),
        trend: allTimeTrend,
        variant: "white",
        diagramType: "bar",
        diagramData: {
          data: (
            dashboardStats?.profitChart || [60, 70, 65, 80, 75, 85, 90]
          ).slice(-LAST_VISUAL_MONTHS),
          width: 100,
          height: 40,
        },
      },
    ];
  }, [topCardsStats, dashboardStats]);

  const statCardsData = useMemo(() => {
    const periodTrend = {
      type: "neutral",
      percentage: 0,
      text: "Filtered period",
    };
    const allTimeTrend = { type: "neutral", percentage: 0, text: "All time" };
    const defaultCards = getDefaultStatCards();

    if (RESTRICTED_STATS_ROLES.includes(role) && topCardsStats != null) {
      const totalLoads = topCardsStats.totalLoads ?? 0;
      const totalDelivered = topCardsStats.totalDeliveredLoads ?? 0;
      const deliveryRate =
        totalLoads > 0 ? Math.round((totalDelivered / totalLoads) * 100) : 0;
      const profit = topCardsStats.totalProfit ?? 0;
      return [
        {
          title: "Total Loads",
          value: formatNumber(totalLoads),
          trend: allTimeTrend,
          variant: "white",
          diagramType: "progress",
          diagramData: {
            value: deliveryRate,
            label: `${deliveryRate}%`,
            size: 60,
          },
        },
        {
          title: "Total Delivered",
          value: formatNumber(totalDelivered),
          trend: allTimeTrend,
          variant: "blue",
          diagramType: "area",
          diagramData: {
            data: (dashboardStats?.revenueChart || [totalDelivered]).slice(
              -LAST_VISUAL_MONTHS,
            ),
            width: 125,
            height: 60,
          },
        },
        {
          title: "Profit",
          value: formatCurrency(profit),
          trend: allTimeTrend,
          variant: "white",
          diagramType: "bar",
          diagramData: {
            data: (dashboardStats?.profitChart || [Math.max(0, profit)]).slice(
              -LAST_VISUAL_MONTHS,
            ),
            width: 100,
            height: 40,
          },
        },
      ];
    }

    const usersScopeActive =
      selectedTab === "users" &&
      filterValues.scopeType === "user" &&
      /^[a-fA-F0-9]{24}$/.test(String(filterValues.scopeId || "").trim());
    const hasUsers =
      selectedTab === "users" && (generalStats != null || loadsStats != null);
    const hasLoads = loadsStats != null || dashboardStats?.loads != null;
    const hasReceivable = selectedTab === "receivable" && paymentsStats != null;
    const hasPayable = selectedTab === "payable" && paymentsStats != null;
    const hasCarriers = carriersStats != null;
    const hasCustomers = customersStats != null;

    const {
      usersData,
      totalUsers: totalUsersResolved,
      topUsers: topUsersResolved,
    } = usersDataResolved;

    if (selectedTab === "users" && hasUsers) {
      const loadsCreated = loadsStats?.loadsCreated ?? 0;
      const delivered = loadsStats?.byStatus?.Delivered ?? 0;
      const profit = loadsStats?.totalProfit ?? 0;
      const deliveryRate =
        loadsCreated > 0 ? Math.round((delivered / loadsCreated) * 100) : 0;
      const top5Loads = (top5Users.length ? top5Users : topUsersResolved)
        .slice(0, 5)
        .map((x) => x.loadsCount ?? x.deliveredCount ?? x.loadCount ?? 0);
      return [
        {
          title: "Total Loads Added",
          value: formatNumber(loadsCreated),
          trend: periodTrend,
          variant: "white",
          diagramType: "progress",
          diagramData: {
            value: deliveryRate,
            label: `${deliveryRate}%`,
            size: 60,
          },
        },
        {
          title: "Total Delivered Loads",
          value: formatNumber(delivered),
          trend: periodTrend,
          variant: "blue",
          diagramType: "area",
          diagramData: {
            data: top5Loads.length ? top5Loads : [delivered],
            width: 125,
            height: 60,
          },
        },
        {
          title: "Total Profit",
          value: formatCurrency(profit),
          trend: periodTrend,
          variant: "white",
          diagramType: "bar",
          diagramData: { data: [profit], width: 100, height: 40 },
        },
      ];
    }

    if (selectedTab === "loads" && hasLoads) {
      const loads = loadsStats ?? dashboardStats?.loads ?? {};
      const total = loads.total ?? 0;
      const delivered =
        loads.byStatus?.Delivered ?? loads.byStatus?.delivered ?? 0;
      const deliveryRate =
        total > 0 ? Math.round((delivered / total) * 100) : 0;
      const profit =
        loadsStats?.totalProfit ?? dashboardStats?.overview?.totalProfit ?? 0;
      const byMonthDelivered = (loadsStats?.byMonth || [])
        .slice(-LAST_VISUAL_MONTHS)
        .map((m) => m.delivered ?? 0);
      return [
        {
          title: "Total Loads",
          value: formatNumber(total),
          trend: periodTrend,
          variant: "white",
          diagramType: "progress",
          diagramData: {
            value: deliveryRate,
            label: `${deliveryRate}%`,
            size: 60,
          },
        },
        {
          title: "Total Delivered",
          value: formatNumber(delivered),
          trend: periodTrend,
          variant: "blue",
          diagramType: "area",
          diagramData: {
            data: byMonthDelivered.length ? byMonthDelivered : [delivered],
            width: 125,
            height: 60,
          },
        },
        {
          title: "Total Profit",
          value: formatCurrency(profit),
          trend: periodTrend,
          variant: "white",
          diagramType: "bar",
          diagramData: { data: [profit], width: 100, height: 40 },
        },
      ];
    }

    if (selectedTab === "receivable" && paymentsStats) {
      const totals = paymentsStats.totals || {};
      const breakdowns = paymentsStats.breakdowns || {};
      const receivableByStatus = breakdowns.receivableByStatus || {};
      const receivableTotal = totals.receivableTotal || 0;
      const receivedAmount =
        (receivableByStatus.received?.amount || 0) +
        (receivableByStatus["partially received"]?.amount || 0);
      const toBeReceived = Math.max(0, receivableTotal - receivedAmount);
      const receivedPercent =
        receivableTotal > 0
          ? Math.min(100, Math.round((receivedAmount / receivableTotal) * 100))
          : 0;
      const points = paymentsStats.points || [];
      const receivedAmounts = points
        .slice(-LAST_VISUAL_MONTHS)
        .map(
          (p) =>
            (p.breakdowns?.receivableByStatus?.received?.amount || 0) +
            (p.breakdowns?.receivableByStatus?.["partially received"]?.amount ||
              0)
        );
      return [
        {
          title: "Total Received",
          value: formatCurrency(receivedAmount),
          trend: periodTrend,
          variant: "white",
          diagramType: "progress",
          diagramData: {
            value: receivedPercent,
            label: `${receivedPercent}%`,
            size: 60,
          },
        },
        {
          title: "Total Receivable",
          value: formatCurrency(receivableTotal),
          trend: periodTrend,
          variant: "blue",
          diagramType: "area",
          diagramData: {
            data: receivedAmounts.length ? receivedAmounts : [receivableTotal],
            width: 125,
            height: 60,
          },
        },
        {
          title: "To Be Received",
          value: formatCurrency(toBeReceived),
          trend: periodTrend,
          variant: "white",
          diagramType: "bar",
          diagramData: { data: [toBeReceived], width: 100, height: 40 },
        },
      ];
    }

    if (selectedTab === "payable" && paymentsStats) {
      const totals = paymentsStats.totals || {};
      const breakdowns = paymentsStats.breakdowns || {};
      const payableByStatus = breakdowns.payableByStatus || {};
      const payableTotal = totals.payableTotal || 0;
      const paidAmount =
        (payableByStatus.paid?.amount || 0) +
        (payableByStatus["partially paid"]?.amount || 0);
      const profit = totals.totalProfit || 0;
      const toBePaid = Math.max(0, payableTotal - paidAmount);
      const paidPercent =
        payableTotal > 0
          ? Math.min(100, Math.round((paidAmount / payableTotal) * 100))
          : 0;
      const points = paymentsStats.points || [];
      const paidAmounts = points
        .slice(-LAST_VISUAL_MONTHS)
        .map(
          (p) =>
            (p.breakdowns?.payableByStatus?.paid?.amount || 0) +
            (p.breakdowns?.payableByStatus?.["partially paid"]?.amount || 0)
        );
      return [
        {
          title: "Total Paid",
          value: formatCurrency(paidAmount),
          trend: periodTrend,
          variant: "white",
          diagramType: "progress",
          diagramData: {
            value: paidPercent,
            label: `${paidPercent}%`,
            size: 60,
          },
        },
        {
          title: "Profit",
          value: formatCurrency(profit),
          trend: periodTrend,
          variant: "blue",
          diagramType: "area",
          diagramData: {
            data: paidAmounts.length ? paidAmounts : [profit],
            width: 125,
            height: 60,
          },
        },
        {
          title: "To Be Paid",
          value: formatCurrency(toBePaid),
          trend: periodTrend,
          variant: "white",
          diagramType: "bar",
          diagramData: { data: [toBePaid], width: 100, height: 40 },
        },
      ];
    }

    if (selectedTab === "carriers" && hasCarriers) {
      const totalCarriers = carriersStats?.summary?.totalCarriers ?? 0;
      const totalLoads = carriersStats?.totalLoads ?? 0;
      const delivered =
        loadsStats?.byStatus?.Delivered ??
        dashboardStats?.loads?.byStatus?.delivered ??
        0;
      const profit = dashboardStats?.overview?.totalProfit ?? 0;
      const topLoads = (carriersStats?.topCarriers || [])
        .slice(0, 7)
        .map((c) => c.loadsCount || 0);
      return [
        {
          title: "Total Carriers",
          value: formatNumber(totalCarriers),
          trend: periodTrend,
          variant: "white",
          diagramType: "progress",
          diagramData: {
            value:
              totalCarriers > 0
                ? Math.min(100, Math.round((totalLoads / totalCarriers) * 10))
                : 0,
            label: `${totalCarriers}`,
            size: 60,
          },
        },
        {
          title: "Total Delivered Loads",
          value: formatNumber(delivered),
          trend: periodTrend,
          variant: "blue",
          diagramType: "area",
          diagramData: {
            data: topLoads.length ? topLoads : [delivered],
            width: 125,
            height: 60,
          },
        },
        {
          title: "Total Profit",
          value: formatCurrency(profit),
          trend: periodTrend,
          variant: "white",
          diagramType: "bar",
          diagramData: {
            data: topLoads.slice(0, 5).length ? topLoads.slice(0, 5) : [profit],
            width: 100,
            height: 40,
          },
        },
      ];
    }

    if (selectedTab === "customers" && hasCustomers) {
      const totalCustomers =
        customersStats?.summary?.totalCustomers ??
        customersStats?.totalLoads ??
        0;
      const totalLoads = customersStats?.totalLoads ?? 0;
      const delivered =
        loadsStats?.byStatus?.Delivered ??
        dashboardStats?.loads?.byStatus?.delivered ??
        0;
      const totalProfit = customersStats?.totalProfit ?? 0;
      const topLoads = (
        customersStats?.topCustomers ||
        customersStats?.byCustomer ||
        []
      )
        .slice(0, 7)
        .map((c) => c.loadsCount ?? c.loadCount ?? 0);
      return [
        {
          title: "Total Customers",
          value: formatNumber(totalCustomers),
          trend: periodTrend,
          variant: "white",
          diagramType: "progress",
          diagramData: {
            value:
              totalCustomers > 0
                ? Math.min(100, Math.round((totalLoads / totalCustomers) * 10))
                : 0,
            label: `${totalCustomers}`,
            size: 60,
          },
        },
        {
          title: "Total Delivered Loads",
          value: formatNumber(delivered),
          trend: periodTrend,
          variant: "blue",
          diagramType: "area",
          diagramData: {
            data: topLoads.length ? topLoads : [delivered],
            width: 125,
            height: 60,
          },
        },
        {
          title: "Total Profit",
          value: formatCurrency(totalProfit),
          trend: periodTrend,
          variant: "white",
          diagramType: "bar",
          diagramData: {
            data: (customersStats?.byCustomer || [])
              .slice(0, 5)
              .map((c) => c.profit || 0)
              .filter(Boolean).length
              ? (customersStats?.byCustomer || [])
                  .slice(0, 5)
                  .map((c) => c.profit || 0)
              : [totalProfit],
            width: 100,
            height: 40,
          },
        },
      ];
    }

    return defaultCards;
  }, [
    role,
    selectedTab,
    filterValues,
    getDefaultStatCards,
    usersDataResolved,
    usersCount,
    top5Users,
    topCardsStats,
    dashboardStats,
    loadsStats,
    generalStats,
    carriersStats,
    customersStats,
    paymentsStats,
  ]);

  const { topUsers: topUsersResolved } = usersDataResolved;

  const statisticsConfig = useMemo(() => {
    if (
      !generalStats &&
      !loadsStats &&
      !carriersStats &&
      !customersStats &&
      !paymentsStats
    ) {
      return {
        users: {
          donut: {
            title: "Users by role",
            totalLabel: "Total users",
            total: usersByRole?.totalUsers ?? usersCount ?? 0,
            segments: [],
          },
          bar: { title: "Loads", legend: [], data: [] },
          line: {
            title: "User's earnings",
            subtitle: "",
            categories: [],
            series: [],
          },
        },
        loads: {
          donut: {
            title: "Total loads by status",
            totalLabel: "Total",
            total: 0,
            segments: [],
          },
          bar: {
            title: "Total vs Delivered by month",
            legend: [
              { label: "Total", slug: "total", key: "total" },
              { label: "Delivered", slug: "delivered", key: "delivered" },
            ],
            data: [],
          },
          line: {
            title: "Taken vs Sold",
            subtitle: "Taken loads (solid) · Sold loads (dotted)",
            categories: [],
            series: [],
          },
        },
        carriers: {
          donut: {
            title: "Carriers",
            totalLabel: "Total",
            total: 0,
            segments: [],
          },
          bar: {
            title: "Carriers Total vs Delivered by month",
            legend: [
              { label: "Total", slug: "total", key: "total" },
              { label: "Delivered", slug: "delivered", key: "delivered" },
            ],
            data: [],
          },
          line: {
            title: "Carrier Performance",
            subtitle: "",
            categories: [],
            series: [],
          },
        },
        customers: {
          donut: {
            title: "Customers (visible to you)",
            totalLabel: "Total",
            total: 0,
            segments: [],
          },
          bar: {
            title: "Total vs Delivered by month",
            legend: [
              { label: "Total", slug: "total", key: "total" },
              { label: "Delivered", slug: "delivered", key: "delivered" },
            ],
            data: [],
          },
          line: {
            title: "Total vs Taken",
            subtitle:
              "Total loads (dotted) · Taken loads, excl. Listed & Cancelled (solid)",
            categories: [],
            series: [],
          },
        },
        receivable: {
          donut: {
            title: "Receivable Status",
            totalLabel: "Total",
            total: 0,
            segments: [],
          },
          bar: { title: "Cash Flow (Receivable)", legend: [], data: [] },
          line: {
            title: "Receivable Velocity",
            subtitle: "",
            categories: [],
            series: [],
          },
        },
        payable: {
          donut: {
            title: "Payable Status",
            totalLabel: "Total",
            total: 0,
            segments: [],
          },
          bar: { title: "Cash Flow (Payable)", legend: [], data: [] },
          line: {
            title: "Payable Velocity",
            subtitle: "",
            categories: [],
            series: [],
          },
        },
        payments: {
          donut: {
            title: "Payments status",
            totalLabel: "Total",
            total: 0,
            segments: [],
          },
          bar: { title: "Cash flow", legend: [], data: [] },
          line: {
            title: "Payment velocity",
            subtitle: "",
            categories: [],
            series: [],
          },
        },
      };
    }

    let calculatedTotalFromBarChart = 0;
    if (
      barChartData?.normalizedPoints &&
      Array.isArray(barChartData.normalizedPoints)
    ) {
      calculatedTotalFromBarChart = barChartData.normalizedPoints.reduce(
        (sum, point) => {
          return sum + (Number(point.total) || 0);
        },
        0,
      );
    }

    const expiredListed =
      loadsStats?.expiredListed ??
      loadsStats?.expiredCount ??
      loadsStats?.byStatus?.Expired ??
      0;
    const loadsSummary =
      loadsStats?.summary ||
      (loadsStats?.total !== undefined
        ? {
            totalLoads: loadsStats.total || 0,
            loadsByStatus: {
              delivered: loadsStats.byStatus?.Delivered ?? 0,
              pickedUp: loadsStats.byStatus?.["Picked Up"] ?? 0,
              listed: loadsStats.byStatus?.Listed ?? 0,
              dispatched: loadsStats.byStatus?.Dispatched ?? 0,
              onHold: loadsStats.byStatus?.["On Hold"] ?? 0,
              cancelled: loadsStats.byStatus?.Cancelled ?? 0,
              expired: loadsStats.byStatus?.Expired ?? expiredListed,
            },
          }
        : {});
    const loadsByStatus = loadsSummary.loadsByStatus || {};
    const totalLoads = loadsStats?.total || loadsSummary.totalLoads || 0;

    const deliveredCount = loadsByStatus.delivered || 0;
    const pickedUpCount = loadsByStatus.pickedUp || 0;
    const listedCount = Math.max(
      0,
      (loadsByStatus.listed || 0) - expiredListed,
    );
    const expiredCount = expiredListed || 0;
    const dispatchedCount = loadsByStatus.dispatched || 0;
    const onHoldCount = loadsByStatus.onHold || 0;
    const cancelledCount = loadsByStatus.cancelled || 0;

    const deliveredPercent =
      totalLoads > 0 ? Math.round((deliveredCount / totalLoads) * 100) : 0;
    const pickedUpPercent =
      totalLoads > 0 ? Math.round((pickedUpCount / totalLoads) * 100) : 0;
    const listedPercent =
      totalLoads > 0 ? Math.round((listedCount / totalLoads) * 100) : 0;
    const expiredPercent =
      totalLoads > 0 ? Math.round((expiredCount / totalLoads) * 100) : 0;
    const dispatchedPercent =
      totalLoads > 0 ? Math.round((dispatchedCount / totalLoads) * 100) : 0;
    const onHoldPercent =
      totalLoads > 0 ? Math.round((onHoldCount / totalLoads) * 100) : 0;
    const cancelledPercent =
      totalLoads > 0 ? Math.round((cancelledCount / totalLoads) * 100) : 0;

    const { usersData, totalUsers: totalUsersResolved } = usersDataResolved;
    return {
      users: (() => {
        const donutTotalUsers = usersByRole?.totalUsers ?? usersCount ?? 0;
        const byRole = usersByRole?.byRole || {};
        const totalN = donutTotalUsers || 1;
        const roleLabels = {
          admin: "Admin",
          manager: "Manager",
          accountingManager: "Accounting Manager",
          accountingIn: "Accounting In",
          accountingOut: "Accounting Out",
          freightBroker: "Freight Broker",
          dispatcher: "Dispatcher",
          "Pre-dispatcher": "Pre-dispatcher",
          partner: "Partner",
          salesAgent: "Sales Agent",
          bidAgent: "Bid Agent",
        };
        const roleSegments = Object.keys(byRole)
          .filter((role) => byRole[role] > 0)
          .map((role) => ({
            label: `${roleLabels[role] || role} (${byRole[role]})`,
            value: Math.round((byRole[role] / totalN) * 100),
            slug: role.replace(/\s+/g, "").toLowerCase(),
          }))
          .sort((a, b) => b.value - a.value);
        const byMonth = (loadsStats?.byMonth || []).slice(-LAST_VISUAL_MONTHS);
        const chartPoints = loadsStats?.chartPoints || [];
        const categories =
          chartPoints.length > 0
            ? chartPoints.map((p) =>
                p.periodKey && p.periodKey.length > 7
                  ? p.periodKey.slice(0, 10)
                  : p.periodKey || "",
              )
            : byMonth.map((m) => m.monthLabel || m.month || "");
        const listedData =
          chartPoints.length > 0
            ? chartPoints.map((p) => p.loadsCreated ?? 0)
            : byMonth.map((m) => m.total ?? 0);
        const deliveredData =
          chartPoints.length > 0
            ? chartPoints.map((p) => p.delivered ?? 0)
            : byMonth.map((m) => m.delivered ?? 0);
        return {
          donut: {
            title: "Users by role",
            totalLabel: "Total users",
            total: donutTotalUsers,
            segments: roleSegments,
          },
          bar: (() => {
            if (
              barChartData?.normalizedPoints &&
              Array.isArray(barChartData.normalizedPoints) &&
              barChartData.normalizedPoints.length > 0
            ) {
              const barPeriod = barChartData.barPeriod || "month";
              const periodLabel =
                barPeriod === "hour"
                  ? "by hour"
                  : barPeriod === "day"
                    ? "by day"
                    : barPeriod === "week"
                      ? "by week"
                      : "by month";
              const mappedData = barChartData.normalizedPoints
                .map((p) => ({
                  label: p.label || p.periodKey || "",
                  total: Number(p.total) || 0,
                  delivered: Number(p.delivered) || 0,
                }))
                .filter((item) => item.label);
              if (mappedData.length > 0) {
                return {
                  title: `Total vs Delivered ${periodLabel}`,
                  legend: [
                    { label: "Total", slug: "total", key: "total" },
                    { label: "Delivered", slug: "delivered", key: "delivered" },
                  ],
                  data: mappedData,
                };
              }
            }
            const byMonth = (loadsStats?.byMonth || []).slice(
              -LAST_VISUAL_MONTHS,
            );
            return {
              title: "Loads by month",
              legend: [
                { label: "Total", slug: "total", key: "total" },
                { label: "Delivered", slug: "delivered", key: "delivered" },
              ],
              data: byMonth.map((m) => ({
                label: m.monthLabel || m.month || "",
                total: m.total ?? 0,
                delivered: m.delivered ?? 0,
              })),
            };
          })(),
          line: {
            title: "User's loads",
            subtitle: "Listed (created) vs Delivered by period",
            categories,
            series: [
              { name: "Listed", slug: "listed", data: listedData },
              { name: "Delivered", slug: "delivered", data: deliveredData },
            ],
          },
        };
      })(),
      loads: {
        donut: {
          title: "Total loads by status",
          totalLabel: "Total",
          total: totalLoads,
          segments: [
            {
              label: `Listed (${listedCount})`,
              value: listedPercent,
              slug: "listed",
              count: listedCount,
            },
            {
              label: `Expired (${expiredCount})`,
              value: expiredPercent,
              slug: "expired",
              count: expiredCount,
            },
            {
              label: `Dispatched (${dispatchedCount})`,
              value: dispatchedPercent,
              slug: "dispatched",
              count: dispatchedCount,
            },
            {
              label: `Picked Up (${pickedUpCount})`,
              value: pickedUpPercent,
              slug: "pickedup",
              count: pickedUpCount,
            },
            {
              label: `Delivered (${deliveredCount})`,
              value: deliveredPercent,
              slug: "delivered",
              count: deliveredCount,
            },
            {
              label: `On Hold (${onHoldCount})`,
              value: onHoldPercent,
              slug: "onhold",
              count: onHoldCount,
            },
            {
              label: `Cancelled (${cancelledCount})`,
              value: cancelledPercent,
              slug: "cancelled",
              count: cancelledCount,
            },
          ],
        },
        bar: (() => {
          if (
            barChartData?.normalizedPoints &&
            Array.isArray(barChartData.normalizedPoints) &&
            barChartData.normalizedPoints.length > 0
          ) {
            const barPeriod = barChartData.barPeriod || "month";
            const periodLabel =
              barPeriod === "hour"
                ? "by hour"
                : barPeriod === "day"
                  ? "by day"
                  : barPeriod === "week"
                    ? "by week"
                    : "by month";
            const mappedData = barChartData.normalizedPoints.map((p) => ({
              label: p.label || p.periodKey || "",
              total:
                p.total !== undefined && p.total !== null ? Number(p.total) : 0,
              delivered:
                p.delivered !== undefined && p.delivered !== null
                  ? Number(p.delivered)
                  : 0,
            }));
            if (mappedData.length > 0) {
              return {
                title: `Total vs Delivered ${periodLabel}`,
                legend: [
                  { label: "Total", slug: "total", key: "total" },
                  { label: "Delivered", slug: "delivered", key: "delivered" },
                ],
                data: mappedData,
              };
            }
          }
          const byMonth = (
            Array.isArray(loadsStats?.byMonth) ? loadsStats.byMonth : []
          ).slice(-LAST_VISUAL_MONTHS);
          if (byMonth && byMonth.length > 0) {
            const monthData = byMonth
              .map((m) => ({
                label: m.monthLabel || m.month || "",
                total: m.total ?? 0,
                delivered: m.delivered ?? 0,
              }))
              .filter((item) => item.label);
            if (monthData.length > 0) {
              return {
                title: "Total vs Delivered by month",
                legend: [
                  { label: "Total", slug: "total", key: "total" },
                  { label: "Delivered", slug: "delivered", key: "delivered" },
                ],
                data: monthData,
              };
            }
          }
          return {
            title: "Total vs Delivered by month",
            legend: [
              { label: "Total", slug: "total", key: "total" },
              { label: "Delivered", slug: "delivered", key: "delivered" },
            ],
            data: [],
          };
        })(),
        line: (() => {
          const rawPoints = Array.isArray(barChartStatusData)
            ? barChartStatusData
            : [];
          const displayPeriod = barChartData?.barPeriod || "day";
          if (rawPoints.length > 0) {
            const sorted = [...rawPoints].sort((a, b) =>
              (a.periodKey || "").localeCompare(b.periodKey || ""),
            );
            const categories = sorted.map(
              (p) =>
                formatBarLabel(p.periodKey, displayPeriod) || p.periodKey || "",
            );
            const byStatus = (p) => p.breakdowns?.loadsByStatus || {};
            const takenData = sorted.map((p) => {
              const b = byStatus(p);
              return (
                (b.dispatched ?? 0) +
                (b.pickedUp ?? 0) +
                (b.delivered ?? 0) +
                (b.onHold ?? 0)
              );
            });
            const soldData = sorted.map((p) => byStatus(p).delivered ?? 0);
            return {
              title: "Taken vs Sold",
              subtitle: "Taken loads (solid) · Sold loads (dotted)",
              categories,
              series: [
                { name: "Taken", slug: "taken", data: takenData },
                { name: "Sold", slug: "sold", data: soldData },
              ],
            };
          }
          const byMonth = (
            Array.isArray(loadsStats?.byMonth) ? loadsStats.byMonth : []
          ).slice(-LAST_VISUAL_MONTHS);
          if (byMonth.length > 0) {
            return {
              title: "Taken vs Sold",
              subtitle: "Taken loads (solid) · Sold loads (dotted)",
              categories: byMonth.map((m) => m.monthLabel || m.month || ""),
              series: [
                {
                  name: "Taken",
                  slug: "taken",
                  data: byMonth.map((m) => m.taken ?? 0),
                },
                {
                  name: "Sold",
                  slug: "sold",
                  data: byMonth.map((m) => m.delivered ?? 0),
                },
              ],
            };
          }
          return {
            title: "Taken vs Sold",
            subtitle: "Taken loads (solid) · Sold loads (dotted)",
            categories: [],
            series: [],
          };
        })(),
      },
      payments: {
        donut: {
          title: "Payments status",
          totalLabel: "Total",
          total:
            (dashboardStats?.payments?.receivable?.totalAmount || 0) +
            (dashboardStats?.payments?.payable?.totalAmount || 0),
          segments: [
            { label: "Paid", value: 62, slug: "paid" }, // TODO: Get from API
            { label: "Outstanding", value: 25, slug: "outstanding" },
            { label: "Overdue", value: 13, slug: "overdue" },
          ],
        },
        bar: {
          title: "Cash flow",
          legend: [
            { label: "Revenue", slug: "incoming", key: "incoming" },
            { label: "Expense", slug: "outgoing", key: "outgoing" },
          ],
          data: [
            { label: "Jan", incoming: 32, outgoing: 18 },
            { label: "Feb", incoming: 34, outgoing: 20 },
            { label: "Mar", incoming: 30, outgoing: 22 },
            { label: "Apr", incoming: 36, outgoing: 24 },
            { label: "May", incoming: 40, outgoing: 28 },
          ].slice(-LAST_VISUAL_MONTHS),
        },
        line: {
          title: "Payment velocity",
          subtitle: "Paid vs projected collections",
          categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"].slice(
            -LAST_VISUAL_MONTHS,
          ),
          series: [
            {
              name: "Paid",
              slug: "this",
              data: [12, 16, 18, 20, 24, 26, 28].slice(-LAST_VISUAL_MONTHS),
            },
            {
              name: "Projected",
              slug: "last",
              data: [14, 18, 20, 22, 25, 28, 30].slice(-LAST_VISUAL_MONTHS),
            },
          ],
        },
      },
      carriers: {
        donut: (() => {
          const byEquipmentType = carriersStats?.summary?.byEquipmentType || {};
          const totalCarriers = carriersStats?.summary?.totalCarriers || 0;
          const totalN = totalCarriers || 1;
          const equipmentSegments = Object.keys(byEquipmentType)
            .filter((key) => byEquipmentType[key] > 0)
            .map((key) => ({
              label: `${key} (${byEquipmentType[key]})`,
              value: Math.round((byEquipmentType[key] / totalN) * 100),
              slug: key.toLowerCase().replace(/\s+/g, ""),
            }))
            .sort((a, b) => b.value - a.value);
          return {
            title: "Carriers",
            totalLabel: "Total",
            total: totalCarriers,
            segments: equipmentSegments,
          };
        })(),
        bar: (() => {
          const byMonth = (
            Array.isArray(loadsStats?.byMonth) ? loadsStats.byMonth : []
          ).slice(-LAST_VISUAL_MONTHS);
          return {
            title: "Carriers Total vs Delivered by month",
            legend: [
              { label: "Total", slug: "total", key: "total" },
              { label: "Delivered", slug: "delivered", key: "delivered" },
            ],
            data: byMonth.map((m) => ({
              label: m.monthLabel || m.month || "",
              total: m.total ?? 0,
              delivered: m.delivered ?? 0,
            })),
          };
        })(),
        line: (() => {
          const byMonth = (Array.isArray(loadsStats?.byMonth) ? loadsStats.byMonth : []).slice(-LAST_VISUAL_MONTHS);
          return {
            title: "Carrier Performance",
            subtitle: "Total vs Delivered by period",
            categories: byMonth.map((m) => m.monthLabel || m.month || ""),
            series: [
              { name: "Total", slug: "total", data: byMonth.map((m) => m.total ?? 0) },
              { name: "Delivered", slug: "delivered", data: byMonth.map((m) => m.delivered ?? 0) },
            ],
          };
        })(),
      },
      customers: {
        donut: (() => {
          const totalCustomers = customersStats?.summary?.totalCustomers ?? 0;
          const active = customersStats?.summary?.active ?? 0;
          const suspended = customersStats?.summary?.suspended ?? 0;
          const inactive = customersStats?.summary?.inactive ?? 0;
          const totalN = totalCustomers || 1;
          const segments = [
            { label: `Active (${active})`, value: Math.round((active / totalN) * 100), slug: "active", count: active },
            { label: `Suspended (${suspended})`, value: Math.round((suspended / totalN) * 100), slug: "suspended", count: suspended },
            { label: `Inactive (${inactive})`, value: Math.round((inactive / totalN) * 100), slug: "inactive", count: inactive },
          ].filter((seg) => seg.count > 0).sort((a, b) => b.value - a.value);
          return {
            title: "Customers (visible to you)",
            totalLabel: "Total",
            total: totalCustomers,
            segments
          };
        })(),
        bar: {
          title: "Total vs Delivered by month",
          legend: [
            { label: "Total", slug: "total", key: "total" },
            { label: "Delivered", slug: "delivered", key: "delivered" },
          ],
          data: (() => {
            const byMonth = (
              Array.isArray(loadsStats?.byMonth) ? loadsStats.byMonth : []
            ).slice(-LAST_VISUAL_MONTHS);
            return byMonth.map((m) => ({
              label: m.monthLabel || m.month || "",
              total: m.total ?? 0,
              delivered: m.delivered ?? 0,
            }));
          })(),
        },
        line: (() => {
          const byMonth = (
            Array.isArray(loadsStats?.byMonth) ? loadsStats.byMonth : []
          ).slice(-LAST_VISUAL_MONTHS);
          return {
            title: "Total vs Taken",
            subtitle:
              "Total loads (dotted) · Taken loads, excl. Listed & Cancelled (solid)",
            categories: byMonth.map((m) => m.monthLabel || m.month || ""),
            series: [
              {
                name: "Taken",
                slug: "taken",
                data: byMonth.map((m) => m.taken ?? 0),
              },
              {
                name: "Total",
                slug: "total",
                data: byMonth.map((m) => m.total ?? 0),
              },
            ],
          };
        })(),
      },
      receivable: (() => {
        if (!paymentsStats) {
          return {
            donut: {
              title: "Receivable Status",
              totalLabel: "Total",
              total: 0,
              segments: [],
            },
            bar: { title: "Cash Flow (Receivable)", legend: [], data: [] },
            line: {
              title: "Receivable Velocity",
              subtitle: "",
              categories: [],
              series: [],
            },
          };
        }
        const totals = paymentsStats.totals || {};
        const breakdowns = paymentsStats.breakdowns || {};
        const receivableByStatus = breakdowns.receivableByStatus || {};
        const receivableTotal = totals.receivableTotal || 0;
        const totalN = receivableTotal || 1;

        const donutSegments = Object.keys(receivableByStatus)
          .filter(
            (status) =>
              receivableByStatus[status]?.count > 0 ||
              receivableByStatus[status]?.amount > 0,
          )
          .map((status) => {
            const statusData = receivableByStatus[status];
            const count = statusData?.count || 0;
            const amount = statusData?.amount || 0;
            const percent =
              totalN > 0 ? Math.round((amount / totalN) * 100) : 0;
            return {
              label: `${formatStatusLabel(status)} (${count}, ${formatCurrency(amount)})`,
              value: percent,
              slug: status.toLowerCase().replace(/\s+/g, ""),
              count,
              amount,
            };
          })
          .sort((a, b) => b.value - a.value);

        const points = paymentsStats.points || [];
        const barByMonth = paymentsStats.barByMonth || [];
        const barData = barByMonth.map((m) => {
          const total = m.receivableTotal ?? 0;
          const confirmed = m.received ?? 0;
          return {
            label: m.monthLabel || m.month || "",
            total,
            confirmed,
          };
        });

        const lineCategories = points
          .slice(-LAST_VISUAL_MONTHS)
          .map((point) => {
            const periodKey = point.periodKey || "";
            return periodKey.length > 7
              ? formatMonthLabel(periodKey)
              : periodKey;
          });
        const receivableTotalSeries = points
          .slice(-LAST_VISUAL_MONTHS)
          .map((point) => point.totals?.receivableTotal || 0);
        const confirmedReceivableSeries = points
          .slice(-LAST_VISUAL_MONTHS)
          .map((point) => {
            const rec = point.breakdowns?.receivableByStatus;
            const received = rec?.received?.amount ?? 0;
            const partial = rec?.["partially received"]?.amount ?? 0;
            if (received > 0 || partial > 0) return received + partial;
            return point.totals?.confirmedReceivable ?? point.breakdowns?.receivable?.money?.confirmed ?? 0;
          });

        return {
          donut: {
            title: "Receivable Status",
            totalLabel: "Total",
            total: receivableTotal,
            segments: donutSegments,
          },
          bar: {
            title: "Cash Flow (Receivable)",
            legend: [
              { label: "Total", slug: "total", key: "total" },
              { label: "Confirmed", slug: "confirmed", key: "confirmed" },
            ],
            data: barData,
          },
          line: {
            title: "Receivable Velocity",
            subtitle: "Total vs Confirmed by period",
            categories: lineCategories,
            series: [
              {
                name: "Total",
                slug: "receivabletotal",
                data: receivableTotalSeries,
              },
              {
                name: "Confirmed",
                slug: "confirmed",
                data: confirmedReceivableSeries,
              },
            ],
          },
        };
      })(),
      payable: (() => {
        if (!paymentsStats) {
          return {
            donut: {
              title: "Payable Status",
              totalLabel: "Total",
              total: 0,
              segments: [],
            },
            bar: { title: "Cash Flow (Payable)", legend: [], data: [] },
            line: {
              title: "Payable Velocity",
              subtitle: "",
              categories: [],
              series: [],
            },
          };
        }
        const totals = paymentsStats.totals || {};
        const breakdowns = paymentsStats.breakdowns || {};
        const payableByStatus = breakdowns.payableByStatus || {};
        const payableTotal = totals.payableTotal || 0;
        const totalN = payableTotal || 1;

        const donutSegments = Object.keys(payableByStatus)
          .filter(
            (status) =>
              payableByStatus[status]?.count > 0 ||
              payableByStatus[status]?.amount > 0,
          )
          .map((status) => {
            const statusData = payableByStatus[status];
            const count = statusData?.count || 0;
            const amount = statusData?.amount || 0;
            const percent =
              totalN > 0 ? Math.round((amount / totalN) * 100) : 0;
            return {
              label: `${formatStatusLabel(status)} (${count}, ${formatCurrency(amount)})`,
              value: percent,
              slug: status.toLowerCase().replace(/\s+/g, ""),
              count,
              amount,
            };
          })
          .sort((a, b) => b.value - a.value);

        const points = paymentsStats.points || [];
        const barByMonth = paymentsStats.barByMonth || [];
        const barData = barByMonth.map((m) => {
          const total = m.payableTotal ?? 0;
          const confirmed = m.paid ?? 0;
          return {
            label: m.monthLabel || m.month || "",
            total,
            confirmed,
          };
        });

        const lineCategories = points
          .slice(-LAST_VISUAL_MONTHS)
          .map((point) => {
            const periodKey = point.periodKey || "";
            return periodKey.length > 7
              ? formatMonthLabel(periodKey)
              : periodKey;
          });
        const payableTotalSeries = points
          .slice(-LAST_VISUAL_MONTHS)
          .map((point) => point.totals?.payableTotal || 0);
        const confirmedPayableSeries = points
          .slice(-LAST_VISUAL_MONTHS)
          .map((point) => {
            const pay = point.breakdowns?.payableByStatus;
            const paid = pay?.paid?.amount ?? 0;
            const partial = pay?.["partially paid"]?.amount ?? 0;
            if (paid > 0 || partial > 0) return paid + partial;
            return point.totals?.confirmedPayable ?? point.breakdowns?.payable?.money?.confirmed ?? 0;
          });

        return {
          donut: {
            title: "Payable Status",
            totalLabel: "Total",
            total: payableTotal,
            segments: donutSegments,
          },
          bar: {
            title: "Cash Flow (Payable)",
            legend: [
              { label: "Total", slug: "total", key: "total" },
              { label: "Confirmed", slug: "confirmed", key: "confirmed" },
            ],
            data: barData,
          },
          line: {
            title: "Payable Velocity",
            subtitle: "Total vs Confirmed by period",
            categories: lineCategories,
            series: [
              {
                name: "Total",
                slug: "payabletotal",
                data: payableTotalSeries,
              },
              {
                name: "Confirmed",
                slug: "confirmed",
                data: confirmedPayableSeries,
              },
            ],
          },
        };
      })(),
    };
  }, [
    usersDataResolved,
    usersCount,
    usersByRole,
    loadsStats,
    dashboardStats,
    carriersStats,
    customersStats,
    paymentsStats,
    filterValues,
    barChartData,
    barChartStatusData,
  ]);

  const activeConfig =
    statisticsConfig[selectedTab] || statisticsConfig[defaultTab];

  const dateLabel = useMemo(() => {
    const today = new Date();
    return today.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  const handleExport = useCallback(() => {
    if (!STATS_EXPORT_TAB_IDS.includes(selectedTab)) return;
    const tabMeta = statisticsTabs.find((t) => t.value === selectedTab);
    const tabTitle = tabMeta?.label || selectedTab;
    const config = statisticsConfig[selectedTab];
    const rows = buildStatisticsExportRows({
      tabTitle,
      selectedDateRange,
      filterValues,
      statCards: statCardsData,
      config,
      top5Users,
      includeTopUsers: selectedTab === "users",
    });
    downloadStatisticsXlsx(
      rows,
      `${selectedTab}-stats-${selectedDateRange}`,
    );
  }, [
    selectedTab,
    statCardsData,
    statisticsConfig,
    selectedDateRange,
    filterValues,
    top5Users,
  ]);

  if (loading) {
    return (
      <Container
        fluid
        style={{ backgroundColor: colors.background, minHeight: "100vh" }}
      >
        <Loading size="large" />
      </Container>
    );
  }

  if (error) {
    const isAccessDenied = error === "Access denied";
    return (
      <Container
        fluid
        style={{ backgroundColor: colors.background, minHeight: "100vh" }}
      >
        <div
          style={{
            padding: "20px",
            textAlign: "center",
            color: isAccessDenied ? colors.text : "red",
          }}
        >
          {isAccessDenied ? "Access denied" : `Error: ${error}`}
        </div>
      </Container>
    );
  }

  return (
    <Container
      fluid
      style={{ backgroundColor: colors.background, minHeight: "100vh" }}
    >
      <div className="statistics-page">
        <div className="stat-cards">
          {statCardsData.map((card, index) => (
            <div className="stat-cards__wrapper" key={index}>
              <StatCard {...card} />
            </div>
          ))}
        </div>

        <div className="statistics-tabs">
          {visibleTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={`statistics-tabs__item${
                tab.value === selectedTab ? " is-active" : ""
              }`}
              onClick={() => setSelectedTab(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <SearchAndFilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onFilterClick={
            ["loads", "users", "receivable", "payable", "carriers", "customers"].includes(selectedTab)
              ? () => setIsStatsFiltersOpen(true)
              : undefined
          }
          onFilterApply={
            ["loads", "users", "receivable", "payable", "carriers", "customers"].includes(selectedTab)
              ? undefined
              : (filters) => updateFilterValues(() => filters)
          }
          initialFilters={filterValues}
          showSearch={false}
          searchPlaceholder={
            selectedTab === "users" ? "Search users..." : "Search customers..."
          }
          addButtonLabel="Export"
          onExportClick={
            STATS_EXPORT_TAB_IDS.includes(selectedTab) ? handleExport : undefined
          }
          filterLabel="Filter By"
          pageType="statistics"
          activeTab={selectedTab}
          disableAddModal
          dateRangeOptions={[
            { label: "Today", value: "today" },
            { label: "This Week", value: "this_week" },
            { label: "This Month", value: "this_month" },
            { label: "This Quarter", value: "this_quarter" },
            { label: "This Year", value: "this_year" },
            { label: "Custom Range", value: "custom" },
          ]}
          dateRangeValue={selectedDateRange}
          dateRangeDetail={
            statisticsDateRangeDisplay?.from && statisticsDateRangeDisplay?.to
              ? `${statisticsDateRangeDisplay.from} – ${statisticsDateRangeDisplay.to}`
              : undefined
          }
          onDateRangeChange={(value) => {
            updateSelectedDateRange(value);
            const tabsWithFilters = ["loads", "users", "receivable", "payable", "carriers", "customers"];
            if (value === "custom" && tabsWithFilters.includes(selectedTab)) {
              setIsStatsFiltersOpen(true);
            }
          }}
        />

        {["loads", "users", "receivable", "payable", "carriers", "customers"].includes(selectedTab) && (
          <StatsFilters
            isOpen={isStatsFiltersOpen}
            onClose={() => setIsStatsFiltersOpen(false)}
            draftFilters={draftFilters}
            onDraftFilterChange={(key, value) => {
              setDraftFilters((prev) => ({ ...prev, [key]: value }));
            }}
            filterSchema={filtersSchemaByTab[selectedTab]}
            userRole={role}
            activeTab={selectedTab}
            onApply={() => {
              const period =
                draftFilters.period ||
                getPeriodPresetFromDateRange(selectedDateRange);
              let dateFrom, dateTo;
              let newDateRange = selectedDateRange;

              if (period === "custom") {
                if (draftFilters.startDate && draftFilters.endDate) {
                  dateFrom = draftFilters.startDate.split("T")[0];
                  dateTo = draftFilters.endDate.split("T")[0];
                  newDateRange = "custom";
                } else {
                  const now = new Date();
                  dateFrom = new Date(now.getFullYear(), now.getMonth(), 1)
                    .toISOString()
                    .split("T")[0];
                  dateTo = new Date(now.getFullYear(), now.getMonth() + 1, 0)
                    .toISOString()
                    .split("T")[0];
                  newDateRange = "custom";
                }
              } else {
                const dateRange = getDateRangeForPeriod(
                  period,
                  draftFilters.startDate ||
                    (filterValues.dateFrom
                      ? new Date(filterValues.dateFrom).toISOString()
                      : null),
                  draftFilters.endDate ||
                    (filterValues.dateTo
                      ? new Date(filterValues.dateTo).toISOString()
                      : null),
                );
                dateFrom = dateRange.startDate.split("T")[0];
                dateTo = dateRange.endDate.split("T")[0];

                if (
                  period !== getPeriodPresetFromDateRange(selectedDateRange)
                ) {
                  const periodMap = {
                    day: "today",
                    week: "this_week",
                    month: "this_month",
                    quarter: "this_quarter",
                    year: "this_year",
                  };
                  newDateRange = periodMap[period] || selectedDateRange;
                }
              }

              const baseFilterValues = {
                ...filterValues,
                dateRange: newDateRange,
                period: period,
                dateFrom: dateFrom,
                dateTo: dateTo,
                scopeType:
                  draftFilters.scope === "global" || !draftFilters.scope
                    ? "global"
                    : draftFilters.scope,
                scopeId: draftFilters.scopeId || null,
              };
              const withTabFilters =
                selectedTab === "loads"
                  ? { ...baseFilterValues, loadStatus: draftFilters.loadStatus || [], shipmentType: draftFilters.shipmentType || null }
                  : selectedTab === "receivable" || selectedTab === "payable"
                    ? { ...baseFilterValues, paymentStatus: draftFilters.paymentStatus || [] }
                    : selectedTab === "carriers"
                      ? { ...baseFilterValues, carrierStatus: draftFilters.carrierStatus || [] }
                      : selectedTab === "customers"
                        ? { ...baseFilterValues, customerStatus: draftFilters.customerStatus || [] }
                        : baseFilterValues;
              updateFilterValues(() => withTabFilters);
              setIsStatsFiltersOpen(false);
            }}
            onClear={() => {
              setDraftFilters({});
              updateFilterValues(() => ({ dateRange: "today", period: "day" }));
            }}
          />
        )}

        <div className="statistics-grid">
          <div className="statistics-grid__donut-col">
            <UsersDonutCard
              title={activeConfig.donut.title}
              totalLabel={activeConfig.donut.totalLabel}
              total={activeConfig.donut.total}
              segments={activeConfig.donut.segments}
            />
            {(selectedTab === "users" || selectedTab === "loads") &&
              Array.isArray(top5Users) &&
              top5Users.length > 0 && (
                <div className="statistics-top-customers">
                  <div className="statistics-top-customers__title">
                    Top 5 users by created loads (status not Listed/Cancelled)
                  </div>
                  <ul className="statistics-top-customers__list">
                    {top5Users.map((u, i) => (
                      <li
                        key={u.userId || i}
                        className="statistics-top-customers__item"
                      >
                        <span className="statistics-top-customers__name">
                          {i + 1}. {u.fullName || "—"}
                        </span>
                        <span className="statistics-top-customers__count">
                          {formatNumber(u.loadsCount ?? 0)} loads
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            {selectedTab === "customers" &&
              Array.isArray(customersStats?.topCustomers) &&
              customersStats.topCustomers.length > 0 && (
                <div className="statistics-top-customers">
                  <div className="statistics-top-customers__title">
                    Top customers by dispatched loads
                  </div>
                  <ul className="statistics-top-customers__list">
                    {customersStats.topCustomers.map((c, i) => (
                      <li
                        key={c.customerId || i}
                        className="statistics-top-customers__item"
                      >
                        <span className="statistics-top-customers__name">
                          {c.companyName || "—"}
                        </span>
                        <span className="statistics-top-customers__count">
                          {formatNumber(c.loadsCount ?? c.loadCount ?? 0)}{" "}
                          dispatched
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </div>
          <LoadsBarCard
            title={activeConfig.bar.title}
            legend={activeConfig.bar.legend}
            data={activeConfig.bar.data}
            valueFormat={selectedTab === "receivable" || selectedTab === "payable" ? "currency" : "number"}
          />
        </div>

        <div className="statistics-bottom">
          <EarningsLineCard
            title={activeConfig.line.title}
            subtitle={activeConfig.line.subtitle}
            series={activeConfig.line.series}
            categories={activeConfig.line.categories}
          />
        </div>
      </div>
    </Container>
  );
};

export default StatisticPage;
